import SellerGiftButton from './Component/SellerGiftButton';
import { ReactNode, useState, useEffect, useRef, useCallback } from "react";
import { Socket } from "socket.io-client";
import styled from "styled-components";

import Layout_Admin from "../../../Component/UI/Layout_Admin";
import AdminPageHeader, { AdminActionButton } from "../../../Component/UI/AdminPageHeader";
import DetailItemList from "./Component/Detail_itemList";
import DetailNotify from "./Component/Detail_notify";
import LiveInfo from "./Component/LiveInfo";
import LiveTimeExtension from "./Component/LiveTimeExtension";
import LiveToolbarIcon from "./Component/LiveToolbarIcon";
import LiveCompactControls from "./Component/LiveCompactControls";
import { LiveShippingAction } from "./Component/LiveSideActions";
import LiveChatPanel from "./Component/LiveChatPanel";
import LiveChatNotices from "./Component/LiveChatNotices";
import { SellerNotice, useSellerChat } from "../../Broadcast/sellerNotice";
import LiveProductSales from "./Component/LiveProductSales";
import LivePurchases, { LiveOrderHistory } from "./Component/LivePurchases";
import Manage_Live from "./Component/Manage_Live";
import LivePreviewWindow from "./Component/LivePreviewWindow";
import LiveOperationsPanel from "./Component/LiveOperationsPanel";
import api from "../../../Component/axios_Interceptor/api";
import url from "../../../Component/server";
import { StoreInfo } from "../../../Component/Interface/Store";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import LiveDetailDialog from "./Component/LiveDetailDialog";
import { captureLiveImage, LiveCaptureSource } from "./captureLiveImage";
import { getBroadcastAppInstallUrl } from "./broadcastAppInstall";
import { useLiveCaptureWindow } from "./useLiveCaptureWindow";

const WorkspaceViewport = styled.div`
  container: fanbird-live-workspace / inline-size;
  min-width: 0;
`;

const Workspace = styled.div<{ $detached: boolean; $chatWidth: number; $previewWidth: number }>`
  --live-card-padding-block: var(--space-2);
  --live-sticky-top: calc(var(--live-header-height, var(--space-12)) + var(--space-3));
  display: grid;
  grid-template-columns: ${props => props.$detached ? `minmax(0, 1fr) 10px ${props.$chatWidth}px` : `minmax(0, 1fr) 10px minmax(280px, min(${props.$chatWidth}px, max(280px, calc(100% - ${props.$previewWidth + 460}px)))) 10px ${props.$previewWidth}px`};
  gap: var(--space-4) var(--space-2);
  align-items: flex-start;
  width: 100%;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    > [role="separator"] { display: none; }
  }

  @container fanbird-live-workspace (max-width: ${props => props.$detached ? props.$chatWidth + 440 : props.$previewWidth + 720}px) {
    grid-template-columns: 1fr;
    > [role="separator"] { display: none; }
    > section { position: static; height: 800px; }
    > div:last-child > div { position: static; max-height: none; overflow: visible; }
  }
`;

const LeftColumn = styled.div`
  &[hidden] { display: none; }
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 12px;
  align-self: stretch;

  @media (max-width: 1100px) {
    align-self: start;
    max-width: 500px;
  }
`;
const ResizeHandle = styled.div`
  &[hidden] { display: none; }
  position: sticky; top: var(--live-sticky-top, 126px); height: calc(100dvh - var(--live-sticky-top, 126px) - 54px); min-height: 0;
  cursor: col-resize; touch-action: none; user-select: none; border-radius: var(--radius-pill);
  display: flex; align-items: center; justify-content: center;
  &::after { content: ''; width: 3px; height: 56px; border-radius: var(--radius-pill); background: var(--color-border-default); }
  &:hover::after, &:focus-visible::after { background: var(--color-text-link); }
  &:focus-visible { outline: 2px solid var(--color-focus-ring); outline-offset: 2px; }
`;

const StickyLeftContent = styled.div`
  position: sticky;
  top: var(--live-sticky-top, 126px);
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: calc(100dvh - var(--live-sticky-top, 126px) - 54px);
  overflow-y: auto;

  @media (max-width: 1100px) {
    position: static;
    max-height: none;
    overflow: visible;
  }
`;

const LivePreviewArea = styled.section`
  box-sizing: border-box;
  min-width: 0;
  padding: var(--live-card-padding-block) var(--space-4);
  border: 1px solid var(--admin-theme-border, #e9e6ef);
  border-radius: 12px;
  background: var(--admin-theme-surface, #ffffff);

  @media (max-width: 1100px) {
    width: 100%;
    max-width: 500px;
  }
`;

const RightColumn = styled.div`
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 12px;
`;

const ProductArea = styled.section`
  box-sizing: border-box;
  min-width: 0;
  padding: var(--live-card-padding-block) var(--space-4);
  margin-bottom: 0;
  overflow: hidden;
  background: var(--admin-theme-surface, #ffffff);
  border: 1px solid var(--admin-theme-border, #e9e6ef);
  border-radius: 12px;
`;

interface RehearsalParticipant {
  seq: number;
  userid: string;
  name: string;
  nickname: string;
  phone?: string | null;
  user_level: string;
  selected: boolean;
  can_join_rehearsal: boolean;
  customer_link_status: 'linked' | 'signup_required' | 'phone_required' | 'ambiguous';
  customer_nickname?: string | null;
}

const rehearsalLinkHelp = (item: RehearsalParticipant) => item.customer_link_status === 'linked'
  ? `계정 연동 성공${item.customer_nickname ? ` · ${item.customer_nickname}` : ''}`
  : item.customer_link_status === 'phone_required' ? '연동 필요 · 계정 관리에서 담당자의 휴대폰 번호를 등록해주세요.'
  : item.customer_link_status === 'ambiguous' ? '연동 필요 · 같은 번호의 일반 고객 계정이 여러 개입니다. 계정 확인이 필요합니다.'
  : '연동 필요 · 해당 번호로 일반 고객 회원가입이 필요합니다.';

const RehearsalAccessPanel = ({ liveSeq, active }: { liveSeq?: string; active: boolean }) => {
  const [items, setItems] = useState<RehearsalParticipant[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [saveMessage, setSaveMessage] = useState("");
  const requestVersion = useRef(0);

  const load = async () => {
    if (!liveSeq) return;
    const request = ++requestVersion.current;
    setLoading(true);
    try {
      const response = await api.get(url.Communication_url.rehearsal_access, { params: { seq: liveSeq } });
      if (request !== requestVersion.current) return;
      const next = Array.isArray(response.data?.items) ? response.data.items : [];
      setItems(next.map((item: RehearsalParticipant) => ({ ...item, selected: item.selected && item.customer_link_status === 'linked' })));
      setError("");
    } catch (err: any) {
      if (request !== requestVersion.current) return;
      setError(err?.response?.data?.message || "리허설 참여 계정을 불러오지 못했습니다.");
    } finally {
      if (request === requestVersion.current) setLoading(false);
    }
  };

  const toggleParticipant = (seq: number) => {
    setItems(current => current.map(item => item.seq === seq ? { ...item, selected: !item.selected } : item));
    setSaveError("");
    setSaveMessage("");
  };

  const save = async () => {
    if (!liveSeq) return;
    const selected = items.filter(item => item.selected);
    if (!selected.length) { setSaveError("초대할 담당자를 선택해주세요."); return; }
    if (selected.some(item => item.customer_link_status !== 'linked')) {
      setSaveError('선택한 담당자의 일반 고객 계정 연동 상태를 확인해주세요.');
      return;
    }
    setSaving(true);
    setSaveError("");
    setSaveMessage("");
    try {
      const response = await api.post(url.Communication_url.rehearsal_invite, {
        seq: Number(liveSeq),
        user_seq: selected.map(item => item.seq),
      });
      setSaveMessage(`${Number(response.data?.invited) || 0}명에게 리허설 초대 알림톡 발송을 요청했습니다.`);
    } catch (err: any) {
      setSaveError(err?.response?.data?.message || "리허설 초대 알림톡을 보내지 못했습니다.");
      await load();
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (active) void load();
    return () => { requestVersion.current += 1; };
  }, [liveSeq, active]);

  return <RehearsalPanel>
    <div className="rehearsal-heading"><div><strong>고객 계정 연동</strong><p>담당자 번호로 가입된 일반 고객 계정을 연결해 초대합니다.</p></div>
      <AdminActionButton disabled={loading || saving} onClick={() => void load()}>연동 상태 새로고침</AdminActionButton></div>
    {loading ? <p role="status">계정을 불러오는 중입니다.</p>
      : error ? <p role="alert">{error}</p>
      : items.length === 0 ? <p>등록된 관리자 계정이 없습니다.</p>
      : <>
        <AccountGrid>{items.map(item => <label key={item.seq}
          className={item.customer_link_status === 'linked' ? item.selected ? 'selected' : '' : 'unlinked'}
          tabIndex={item.customer_link_status === 'linked' ? undefined : 0}
          aria-describedby={`rehearsal-account-help-${item.seq}`}>
          <input type="checkbox" aria-label={`${item.nickname || item.name || item.userid} 초대 선택`}
            disabled={saving || item.customer_link_status !== 'linked'} checked={item.selected && item.customer_link_status === 'linked'} onChange={() => toggleParticipant(item.seq)} />
          <AccountText>
            <AccountName className="account-name" title={item.nickname || item.name || item.userid}>{item.nickname || item.name || item.userid}</AccountName>
            <small>{item.user_level === "master" ? "마스터" : "담당자"}{item.phone ? ` · ${item.phone}` : " · 연락처 미등록"}</small>
            <span className="account-link-status">{item.customer_link_status === 'linked' ? rehearsalLinkHelp(item) : '연동 필요'}</span>
          </AccountText>
          <span id={`rehearsal-account-help-${item.seq}`} className="account-link-tooltip" role="tooltip">{rehearsalLinkHelp(item)}</span>
        </label>)}</AccountGrid>
        {items.some(item => !item.phone) && <p>연락처가 없는 담당자는 <a href="/admin/staff">계정 관리</a>에서 휴대폰 번호를 등록한 뒤 초대할 수 있습니다.</p>}
        <SaveRow><span role={saveError ? "alert" : "status"}>{saveError || saveMessage}</span><AdminActionButton $primary disabled={saving || loading || !items.some(item => item.selected && item.customer_link_status === 'linked')} onClick={() => void save()}>{saving ? "초대 발송 중…" : "선택 담당자에게 카카오톡 초대 보내기"}</AdminActionButton></SaveRow>
      </>}
  </RehearsalPanel>;
};

const RehearsalPanel = styled.section`
  padding: var(--space-4);
  border: 1px solid var(--color-border-default);
  border-radius: var(--radius-lg);
  background: var(--color-bg-surface);
  .rehearsal-heading { display: flex; align-items: center; justify-content: space-between; gap: var(--space-3); margin-bottom: var(--space-4); }
  strong { color: var(--color-text-primary); font-size: var(--text-body-lg); font-weight: 600; }
  p { margin: var(--space-2) 0; color: var(--color-text-secondary); font-size: var(--text-caption); }
  p[role="alert"] { color: var(--color-text-danger); }
  @media(max-width: 640px) { .rehearsal-heading { align-items: flex-start; flex-direction: column; } }
`;
const AccountGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: var(--space-3);
  label {
    position: relative;
    display: flex;
    align-items: center;
    gap: var(--space-3);
    min-width: 0;
    min-height: calc(var(--control-lg) + var(--space-6));
    padding: var(--space-3) var(--space-4);
    border: 1px solid var(--color-border-default);
    border-radius: var(--radius-md);
    background: var(--color-bg-surface);
    cursor: pointer;
  }
  label.selected { border-color: var(--color-brand); background: var(--color-brand-subtle); }
  label:hover { border-color: var(--color-brand); }
  label.unlinked { border-color: var(--color-border-danger); cursor: help; }
  label.unlinked .account-name { color: var(--color-text-danger); }
  label:focus-visible { outline: 2px solid var(--color-focus-ring); outline-offset: 2px; }
  input { flex: 0 0 var(--space-5); width: var(--space-5); height: var(--space-5); accent-color: var(--color-brand); }
  .account-link-status { color: var(--color-success); font-size: var(--text-caption); overflow-wrap: anywhere; }
  .unlinked .account-link-status { color: var(--color-text-danger); }
  .account-link-tooltip {
    display: none; position: absolute; inset-inline: 0; top: calc(100% + var(--space-1)); z-index: var(--z-tooltip);
    padding: var(--space-3); border-radius: var(--radius-md); background: var(--color-bg-inverse); color: var(--color-text-on-inverse);
    font-size: var(--text-caption); line-height: var(--leading-caption); box-shadow: var(--shadow-md); pointer-events: none;
  }
  label:hover .account-link-tooltip, label:focus-within .account-link-tooltip { display: block; }
  @media(max-width: 560px) { grid-template-columns: 1fr; }
`;
const AccountText = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  min-width: 0;
  small { color: var(--color-text-secondary); font-size: var(--text-caption); overflow-wrap: anywhere; }
`;
const AccountName = styled.span`
  color: var(--color-text-primary);
  font-size: var(--text-body);
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const SaveRow = styled.div`display:flex;align-items:center;justify-content:space-between;gap:var(--space-3);margin-top:var(--space-4);>span{color:var(--color-text-secondary);font-size:var(--text-caption);}>[role="alert"]{color:var(--color-text-danger);}@media(max-width:520px){align-items:stretch;flex-direction:column;button{width:100%;}}`;

type BroadcastWorkspace = {
  renderPreview: (chatTarget: HTMLDivElement | null, sellerNotice: string, totalSales: number | null | undefined) => ReactNode;
  status: string | null;
  socket: Socket | null;
  refreshVersion: number;
  captureReady: boolean;
};

const loadSellerNotice = (liveSeq: number) => api.get<SellerNotice[]>(url.Communication_url.get_live_notice, { params: { seq: liveSeq, type: "sn" } }).then(response => response.data);

const LiveDetail = ({ adminPermissions = [], adminLevel, broadcast }: {
  adminPermissions?: string[];
  adminLevel?: string;
  broadcast?: BroadcastWorkspace;
}) => {
  const Navigate = useNavigate();
  const { seq } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const viewingOrders = adminLevel === 'master' && searchParams.get('tab') === 'orders';
  const selectTab = (orders: boolean) => {
    setSearchParams(current => { const next = new URLSearchParams(current); if (orders) next.set('tab', 'orders'); else next.delete('tab'); return next; }, { replace: true });
  };
  const sellerChat = useSellerChat(Number(seq), loadSellerNotice, adminLevel === 'master' || (adminLevel === 'cs' && adminPermissions.includes('menu_live')));
  const sellerNotice = sellerChat.messages.at(-1)?.content ?? '';
  const [store_info, setStoreInfo] = useState<StoreInfo>();
  const [notifyType, setNotifyType] = useState("ln");
  const [live_cancle_check, setLive_cancle_check] = useState(false);
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [sectionDialog, setSectionDialog] = useState<'info' | 'operations' | 'rehearsal' | null>(null);
  const headerRef = useRef<HTMLElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [timeVersion, setTimeVersion] = useState(0);
  const [purchaseVersion, setPurchaseVersion] = useState(0);
  const purchaseChanged = useCallback(() => setPurchaseVersion(value => value + 1), []);
  useEffect(() => {
    const header = headerRef.current; if (!header) return;
    let frame = 0;
    const measure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const height = Math.ceil(header.getBoundingClientRect().height);
        if (height > 0) setHeaderHeight(previous => previous === height ? previous : height);
      });
    };
    measure();
    // Recheck when scrolling as well, including restored tabs and popup return.
    const scroller = header.closest('.children');
    scroller?.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(header);
    return () => { cancelAnimationFrame(frame); observer?.disconnect(); scroller?.removeEventListener('scroll', measure); window.removeEventListener('resize', measure); };
  });
  const workspaceRef = useRef<HTMLDivElement>(null);
  const resizing = useRef<{ x: number; width: number } | null>(null);
  const previewResizing = useRef<{ x: number; width: number; chat: number } | null>(null);
  const chatColumnRef = useRef<HTMLElement>(null);
  const previewColumnRef = useRef<HTMLDivElement>(null);
  const [previewWidth, setPreviewWidth] = useState(() => {
    try { const saved = Number(localStorage.getItem('fanbird.admin.live.previewWidth')); return saved >= 220 && saved <= 550 ? saved : 320; }
    catch { return 320; }
  });
  useEffect(() => { try { localStorage.setItem('fanbird.admin.live.previewWidth', String(previewWidth)); } catch { /* Resizing still works. */ } }, [previewWidth]);
  const adjustPreview = (width: number, chat: number, delta: number) => {
    const limitedDelta = Math.max(Math.max(220 - width, chat - 700), Math.min(Math.min(550 - width, chat - 280), delta));
    setPreviewWidth(Math.round(width + limitedDelta)); setChatWidth(Math.round(chat - limitedDelta));
  };
  const [chatWidth, setChatWidth] = useState(() => {
    try { const saved = Number(localStorage.getItem('fanbird.admin.live.chatWidth')); return saved >= 280 && saved <= 700 ? saved : 380; }
    catch { return 380; }
  });
  const resizeChat = (width: number) => setChatWidth(Math.round(Math.max(280, Math.min(700, (workspaceRef.current?.clientWidth || 1400) * .55, width))));
  useEffect(() => { try { localStorage.setItem('fanbird.admin.live.chatWidth', String(chatWidth)); } catch { /* Resizing still works without saved preferences. */ } }, [chatWidth]);
  const { captureTarget, captureWindowError, openCapture, closeCapture } = useLiveCaptureWindow(seq);
  const [previewDetached, setPreviewDetached] = useState(false);
  const [previewToolbar, setPreviewToolbar] = useState<HTMLDivElement | null>(null);
  const [chatTarget, setChatTarget] = useState<HTMLDivElement | null>(null);
  const livePreviewRef = useRef<HTMLDivElement>(null);
  const broadcasting = !!broadcast;
  const captureReady = broadcast?.captureReady;
  const broadcastStatus = broadcast?.status;
  const getLiveVideo = useCallback(() => broadcasting && !captureReady ? null
    : livePreviewRef.current?.querySelector<LiveCaptureSource>('video, canvas') ?? null, [broadcasting, captureReady]);
  const captureImage = useCallback(() => captureLiveImage(getLiveVideo()), [getLiveVideo]);
  const canRunLive = adminLevel === 'master' || adminPermissions.includes("live_run");
  const canManageProducts = adminLevel === 'master' || adminPermissions.includes("product_manage");
  const [liveState, setLiveState] = useState<{ seq?: string; status: string | null; canRestart?: boolean }>({ status: null });
  const updateLiveStatus = useCallback((status: string | null, canRestart = false) => setLiveState({ seq, status, canRestart }), [seq]);
  useEffect(() => { if (broadcasting) updateLiveStatus(broadcastStatus ?? null); }, [broadcasting, broadcastStatus, updateLiveStatus]);
  const broadcastAppInstallUrl = getBroadcastAppInstallUrl();
  const salesRehearsal = liveState.seq === seq && liveState.status === '리허설';
  const [salesSummary, setSalesSummary] = useState<{ seq?: string; rehearsal: boolean; total: number | null }>({ rehearsal: false, total: null });
  const updateSalesTotal = useCallback((total: number | null) => setSalesSummary({ seq, rehearsal: salesRehearsal, total }), [seq, salesRehearsal]);
  const totalSales = adminLevel === 'master'
    ? salesSummary.seq === seq && salesSummary.rehearsal === salesRehearsal ? salesSummary.total : null
    : undefined;
  const registrationOpen = liveState.seq === seq && ['예약', '리허설', '방송중'].includes(liveState.status || '');
  const broadcastOpen = registrationOpen || (liveState.seq === seq && liveState.status === '종료' && liveState.canRestart === true);
  const disabledReason = liveState.status === '종료'
    ? '종료된 라이브에서는 상품 추가를 할 수 없습니다.'
    : liveState.status === '취소' ? '취소된 라이브에서는 상품 추가를 할 수 없습니다.'
    : '라이브 상태를 확인한 후 이용할 수 있습니다.';


  const [paymentMode, setPaymentMode] = useState<'pg' | 'combined' | 'bank'>('pg');
  const [, setChange] = useState(0);

  const live_auth_check = async () => {
    const res = await api.post(url.Communication_url.live_auth_check, {
      seq: seq,
    });
    if (res.data === "n") {
      Navigate("/admin/livelist");
    } else {
      get_store_info();
    }
  };

  const get_store_info = async () => {
    try {
      const res = await api.get(url.Communication_url.store_info);
      setStoreInfo(res.data);
    } catch (error) {
      console.error("데이터 로드 실패:", error);
    }
  };

  useEffect(() => {
    live_auth_check();
  }, []);

  const [couponActionTarget, setCouponActionTarget] = useState<HTMLDivElement | null>(null);
  return (
    <Layout_Admin menuname={"라이브 목록"} collapsibleSidebar compactTop coinRefreshVersion={timeVersion}>
      <LiveCompactControls />
      <LivePageHeader
        elementRef={headerRef}
        title={broadcast ? '라이브 방송 관리' : '라이브 상세보기'}
        onBack={() => Navigate("/admin/livelist")}
        actions={<HeaderToolbar>
          <PreviewActions ref={setPreviewToolbar} />
          <ManagementActions>
          <AdminActionButton $accented type="button" aria-haspopup="dialog" onClick={() => setSectionDialog('info')}><LiveToolbarIcon name="info" />라이브 방송 정보</AdminActionButton>
          <AdminActionButton $accented type="button" aria-haspopup="dialog" onClick={() => setSectionDialog('operations')}><LiveToolbarIcon name="tools" />방송 운영 도구</AdminActionButton>
          <AdminActionButton $accented type="button" aria-haspopup="dialog" onClick={() => setSectionDialog('rehearsal')}><LiveToolbarIcon name="people" />리허설 담당자</AdminActionButton>
          </ManagementActions>
          {canRunLive && <BroadcastActions>
          {adminLevel === 'master' && seq && <LiveTimeExtension liveSeq={Number(seq)} disabled={!registrationOpen} onExtended={() => setTimeVersion(value => value + 1)} />}
          {!broadcast && seq && <AdminActionButton
            as={broadcastOpen ? 'a' : 'button'}
            $primary
            data-live-broadcast
            disabled={!broadcastOpen}
            title={broadcastOpen ? undefined : liveState.status === '종료'
              ? '라이브는 예약 시작 5분 전부터 예약 종료 전까지 다시 송출할 수 있습니다.' : disabledReason}
            href={broadcastOpen ? (broadcastAppInstallUrl ? "/admin/broadcast-app" : `/admin/livelist/Live_manage/${encodeURIComponent(seq)}`) : undefined}
            target={broadcastAppInstallUrl ? undefined : "_blank"}
            rel="noopener noreferrer"
            aria-label={broadcastAppInstallUrl ? "PC 송출 (앱 설치 페이지로 이동)" : "PC 송출 (새 탭에서 열기)"}
            style={{ textDecoration: 'none' }}
          ><LiveToolbarIcon name="broadcast" />PC 송출</AdminActionButton>}
          </BroadcastActions>}
        </HeaderToolbar>}
      />
      {captureWindowError && <p role="alert" style={{ color: 'var(--color-text-danger)' }}>{captureWindowError}</p>}
      {adminLevel === 'master' && <WorkspaceTabs aria-label="라이브 상세 메뉴">
        <button type="button" id="live-products-tab" aria-pressed={!viewingOrders} onClick={() => selectTab(false)}><LiveToolbarIcon name="image" />상품 관리</button>
        <LivePurchases liveSeq={Number(seq)} refreshVersion={purchaseVersion} history canGift={adminLevel === 'master' || adminPermissions.includes('order_manage')} giftDisabled={liveState.seq !== seq || liveState.status !== '방송중'} />
      </WorkspaceTabs>}
      <div id="live-products-panel" role={adminLevel === 'master' ? 'tabpanel' : undefined} aria-labelledby={adminLevel === 'master' ? 'live-products-tab' : undefined} hidden={viewingOrders}>
      <WorkspaceViewport>
      <Workspace style={{ '--live-header-height': headerHeight > 0 ? `${headerHeight}px` : undefined } as React.CSSProperties} ref={workspaceRef} $detached={previewDetached} $chatWidth={chatWidth} $previewWidth={previewWidth}>


        <RightColumn>
          <ProductArea aria-label="라이브 상품 목록 및 관리">
            <DetailItemList
              rehearsal={liveState.seq === seq && liveState.status === '리허설'}
              liveSocket={broadcast?.socket}
              refreshVersion={broadcast?.refreshVersion}
              compact
              buttonChoices
              cancelCouponTarget={canRunLive ? setCouponActionTarget : undefined}
              onInventoryChange={purchaseChanged}
              liveStatus={liveState.seq === seq ? liveState.status || '' : ''}
              canManage={canManageProducts}
              canRegister={registrationOpen}
              registrationDisabledReason={disabledReason}
              captureImage={captureImage}
              getLiveVideo={getLiveVideo}
              captureModalOpen={!!captureTarget}
              captureTarget={captureTarget}
              onCaptureModalClose={closeCapture}
              paymentMode={paymentMode}
              setChange_h={setChange}
              live_cancle_check={live_cancle_check}
              setLive_cancle_check={setLive_cancle_check}
              store_info={store_info || ({} as StoreInfo)}
            />
          </ProductArea>
        </RightColumn>
        <ResizeHandle role="separator" aria-label="상품 목록과 채팅 너비 조절" aria-orientation="vertical" tabIndex={0}
          aria-valuemin={280} aria-valuemax={700} aria-valuenow={chatWidth}
          onPointerDown={event => { event.preventDefault(); resizing.current = { x: event.clientX, width: chatWidth }; event.currentTarget.setPointerCapture?.(event.pointerId); }}
          onPointerMove={event => { if (resizing.current) resizeChat(resizing.current.width + resizing.current.x - event.clientX); }}
          onPointerUp={() => { resizing.current = null; }} onPointerCancel={() => { resizing.current = null; }}
          onLostPointerCapture={() => { resizing.current = null; }}
          onKeyDown={event => { if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') { event.preventDefault(); resizeChat(chatWidth + (event.key === 'ArrowLeft' ? 20 : -20)); } }} />
        <LiveChatColumn ref={chatColumnRef}>
          {adminLevel === 'master' && <LiveProductSales key={`sales-${seq}`} liveSeq={Number(seq)}
            totalSales={totalSales} rehearsal={salesRehearsal} refreshVersion={purchaseVersion} onTotalChange={updateSalesTotal} />}
          <LiveChatPanel key={`chat-${seq}`} liveSeq={Number(seq)} notices={<LiveChatNotices liveSeq={Number(seq)} />} chatRef={setChatTarget} onOpenNotice={canRunLive ? () => setNoticeOpen(true) : undefined} />
        </LiveChatColumn>
        <ResizeHandle hidden={previewDetached} role="separator" aria-label="채팅과 라이브 화면 너비 조절" aria-orientation="vertical" tabIndex={0}
          aria-valuemin={220} aria-valuemax={550} aria-valuenow={previewWidth}
          onPointerDown={event => { event.preventDefault(); previewResizing.current = { x: event.clientX, width: previewColumnRef.current?.clientWidth || previewWidth, chat: chatColumnRef.current?.clientWidth || chatWidth }; event.currentTarget.setPointerCapture?.(event.pointerId); }}
          onPointerMove={event => { const start = previewResizing.current; if (start) adjustPreview(start.width, start.chat, start.x - event.clientX); }}
          onPointerUp={() => { previewResizing.current = null; }} onPointerCancel={() => { previewResizing.current = null; }}
          onLostPointerCapture={() => { previewResizing.current = null; }}
          onKeyDown={event => { if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') { event.preventDefault(); adjustPreview(previewColumnRef.current?.clientWidth || previewWidth, chatColumnRef.current?.clientWidth || chatWidth, event.key === 'ArrowLeft' ? 20 : -20); } }} />
        <LeftColumn ref={previewColumnRef} hidden={previewDetached}>
          <StickyLeftContent>
            <LivePreviewArea aria-label="라이브 화면">
              <LivePreviewWindow key={seq} ref={livePreviewRef}
                onDetachedChange={setPreviewDetached} toolbarTarget={previewToolbar}
                onCapture={canManageProducts ? openCapture : undefined}>
                {broadcast ? broadcast.renderPreview(chatTarget, sellerNotice, undefined)
                  : <Manage_Live seq={seq} sellerNotice={sellerNotice} chatTarget={chatTarget} canManageProducts={canManageProducts} canSendStartNotification={canRunLive} onLiveStatusChange={updateLiveStatus} />}
              </LivePreviewWindow>
            </LivePreviewArea>
          </StickyLeftContent>
        </LeftColumn>
      </Workspace>
      </WorkspaceViewport>
      </div>
      {viewingOrders && <OrdersArea id="live-orders-panel" role="tabpanel" aria-labelledby="live-orders-tab">
        <LiveOrderHistory liveSeq={Number(seq)} refreshVersion={purchaseVersion} canGift={adminLevel === 'master' || adminPermissions.includes('order_manage')} giftDisabled={liveState.seq !== seq || liveState.status !== '방송중'} />
      </OrdersArea>}

      <LiveDetailDialog open={sectionDialog === 'rehearsal'} onClose={() => setSectionDialog(null)} title="리허설 담당자" size="large" keepMounted>
        <RehearsalAccessPanel liveSeq={seq} active={sectionDialog === 'rehearsal'} />
      </LiveDetailDialog>
      <LiveDetailDialog open={sectionDialog === 'info'} onClose={() => setSectionDialog(null)} title="라이브 방송 정보" size="large" keepMounted>
        <LiveInfo refreshVersion={timeVersion} disabled={!registrationOpen} canEditPayment={canRunLive} canEditTitle={canRunLive} canEditCapacity={adminLevel === 'master'}
          canCancel={adminLevel === 'master' || adminPermissions.includes('live_manage')} onCancelled={() => { updateLiveStatus('취소'); setTimeVersion(value => value + 1); }}
          canEditImage={adminLevel === 'master'} onPaymentPolicyChange={setPaymentMode}
          setLive_cancle_check={setLive_cancle_check} />
      </LiveDetailDialog>
      <LiveDetailDialog open={sectionDialog === 'operations'} onClose={() => setSectionDialog(null)} title="방송 운영 도구" size="medium" width={560} keepMounted>
        <RightColumn>
          {canRunLive && <LiveOperationsPanel couponActionTarget={couponActionTarget} liveStatus={liveState.seq === seq ? liveState.status || "" : ""} storeInfo={store_info || ({} as StoreInfo)} />}
          <LiveShippingAction disabled={!registrationOpen} />
          {(adminLevel === 'master' || adminPermissions.includes('order_manage')) && <SellerGiftButton liveSeq={Number(seq)} disabled={liveState.seq !== seq || liveState.status !== '방송중'} />}
        </RightColumn>
      </LiveDetailDialog>
      <LiveDetailDialog
        open={noticeOpen}
        onClose={() => setNoticeOpen(false)}
        title="공지사항 작성·관리"
        description="긴급사항, 공지사항과 셀러 채팅을 관리합니다."
        size="large"
      >
        <DetailNotify notifyType={notifyType} setNotifyType={setNotifyType} onSaved={() => setNoticeOpen(false)} />
      </LiveDetailDialog>
    </Layout_Admin>
  );
};

export default LiveDetail;

const WorkspaceTabs = styled.div.attrs<{ 'data-admin-tabs'?: string }>({ 'data-admin-tabs': '' })`
  display: flex; align-items: center; gap: var(--space-2); margin-bottom: var(--space-3);
  > button { display: inline-flex; align-items: center; gap: var(--space-2); min-height: var(--control-sm);
    padding: 0 var(--space-3); border: 1px solid var(--color-border-default); border-radius: var(--radius-md);
    background: var(--color-bg-surface); color: var(--color-text-secondary); font: inherit; cursor: pointer; }
  > button[aria-pressed="true"] { border-color: var(--color-brand); background: var(--color-brand-subtle); color: var(--color-text-link); }
  > button:focus-visible { outline: 2px solid var(--color-focus-ring); outline-offset: 2px; }
`;
const OrdersArea = styled.section`min-width: 0;`;

const LiveChatColumn = styled.section`
  position: sticky; top: var(--live-sticky-top, 126px); align-self: start;
  display: flex; flex-direction: column; gap: var(--space-3); min-width: 0;
  height: calc(100dvh - var(--live-sticky-top, 126px) - 54px);
  > [aria-label="상품별 판매 현황"] {
    box-sizing: border-box; flex-shrink: 0; min-height: 0; max-height: 40%; margin-bottom: 0;
    overflow: auto; overscroll-behavior: contain;
  }
  @media (max-width: 900px) { position: static; height: 800px; }
`;

const LivePageHeader = styled(AdminPageHeader)`
  min-height: var(--space-12);
  padding-block: var(--space-1);
  margin-bottom: var(--space-3);
  > div:last-child { min-height: var(--control-sm); }
  && .admin-header-action {
    height: var(--control-sm); min-height: var(--control-sm); font-size: var(--text-caption);
    padding-inline: var(--space-2);
    svg { flex-shrink: 0; }
  }
  && button.admin-header-action:not([data-live-broadcast]) {
    border-color: var(--color-border-default); color: var(--color-text-primary);
    svg { color: var(--color-text-link); }
    &:hover:not(:disabled) { border-color: var(--color-brand); }
    &:disabled svg { color: var(--color-text-disabled); }
  }
`;
const HeaderToolbar = styled.div`
  display: flex; flex-wrap: wrap; align-items: center; justify-content: flex-end;
  gap: var(--space-3); min-width: 0;
`;
const ManagementActions = styled.div`
  display: flex; flex-wrap: wrap; align-items: center; gap: var(--space-2);
`;
const BroadcastActions = styled(ManagementActions)`
  border-left: 1px solid var(--color-border-default); padding-left: var(--space-3);
`;
const PreviewActions = styled(ManagementActions)`&:empty { display: none; }`;
